import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { usePlan } from "../../contexts/PlanContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Calendar as CalendarIcon,
    MoreHorizontal,
    FolderKanban,
} from "lucide-react";
import { cn, getStatusColor, getPriorityColor, statusLabels, priorityLabels, formatDate, formatCurrency } from "../../lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Pagination } from "../../components/ui/pagination-control";

const initialFormData = {
    name: "",
    description: "",
    client_id: "",
    start_date: null,
    end_date: null,
    budget: "",
    status: "planning",
    priority: "medium",
};

export const ProjectsPage = () => {
    const navigate = useNavigate();
    const { get, post, del } = useApi();
    const { canCreate, getUsage, refreshPlan } = usePlan();
    const [projects, setProjects] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 12;

    const fetchData = useCallback(async () => {
        const results = await Promise.allSettled([
            get("/projects"),
            get("/clients"),
        ]);
        if (results[0].status === "fulfilled") setProjects(results[0].value);
        else toast.error("Erreur lors du chargement des projets");
        if (results[1].status === "fulfilled") setClients(results[1].value);
        setLoading(false);
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                budget: formData.budget ? parseFloat(formData.budget) : null,
                client_id: formData.client_id || null,
            };
            await post("/projects", payload);
            toast.success("Projet créé avec succès");
            setDialogOpen(false);
            setFormData(initialFormData);
            fetchData();
            refreshPlan();
        } catch (error) {
            toast.error("Erreur lors de la création du projet");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (projectId) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce projet ?")) return;

        try {
            await del(`/projects/${projectId}`);
            toast.success("Projet supprimé");
            fetchData();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const filteredProjects = projects.filter((project) => {
        const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = statusFilter === "all" || project.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredProjects.length / PAGE_SIZE);
    const paginatedProjects = filteredProjects.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="projects-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Projets</h1>
                    <p className="text-muted-foreground mt-1">{projects.length} projets au total</p>
                </div>

                {(() => {
                    const projUsage = getUsage("projects");
                    const canCreateProject = canCreate("projects");
                    return (
                        <div className="flex items-center gap-3">
                            {projUsage.limit !== -1 && (
                                <span className={`text-xs font-mono ${!canCreateProject ? "text-red-500 font-semibold" : "text-muted-foreground"}`} data-testid="project-usage-badge">
                                    {projUsage.current}/{projUsage.limit}
                                </span>
                            )}
                            <Dialog open={dialogOpen} onOpenChange={(o) => { if (!canCreateProject && o) { toast.error("Limite de projets atteinte. Veuillez upgrader votre plan."); navigate("/settings/subscription"); return; } setDialogOpen(o); }}>
                                <DialogTrigger asChild>
                                    <Button data-testid="add-project-btn" disabled={!canCreateProject}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nouveau projet
                                    </Button>
                                </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Nouveau projet</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="name">Nom du projet *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        data-testid="project-name-input"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows={3}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Client</Label>
                                        <Select
                                            value={formData.client_id}
                                            onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                                        >
                                            <SelectTrigger data-testid="project-client-select">
                                                <SelectValue placeholder="Sélectionner..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {clients.map((client) => (
                                                    <SelectItem key={client.client_id} value={client.client_id}>
                                                        {client.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <Label htmlFor="budget">Budget (€)</Label>
                                        <Input
                                            id="budget"
                                            type="number"
                                            value={formData.budget}
                                            onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                                            data-testid="project-budget-input"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Date de début</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    className={cn(
                                                        "w-full justify-start text-left font-normal",
                                                        !formData.start_date && "text-muted-foreground"
                                                    )}
                                                >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {formData.start_date
                                                        ? format(formData.start_date, "dd/MM/yyyy", { locale: fr })
                                                        : "Sélectionner"}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <Calendar
                                                    mode="single"
                                                    selected={formData.start_date}
                                                    onSelect={(date) => setFormData({ ...formData, start_date: date })}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    <div>
                                        <Label>Date de fin</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    className={cn(
                                                        "w-full justify-start text-left font-normal",
                                                        !formData.end_date && "text-muted-foreground"
                                                    )}
                                                >
                                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                                    {formData.end_date
                                                        ? format(formData.end_date, "dd/MM/yyyy", { locale: fr })
                                                        : "Sélectionner"}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0">
                                                <Calendar
                                                    mode="single"
                                                    selected={formData.end_date}
                                                    onSelect={(date) => setFormData({ ...formData, end_date: date })}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Statut</Label>
                                        <Select
                                            value={formData.status}
                                            onValueChange={(value) => setFormData({ ...formData, status: value })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="planning">Planification</SelectItem>
                                                <SelectItem value="in_progress">En cours</SelectItem>
                                                <SelectItem value="on_hold">En pause</SelectItem>
                                                <SelectItem value="completed">Terminé</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div>
                                        <Label>Priorité</Label>
                                        <Select
                                            value={formData.priority}
                                            onValueChange={(value) => setFormData({ ...formData, priority: value })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="low">Basse</SelectItem>
                                                <SelectItem value="medium">Moyenne</SelectItem>
                                                <SelectItem value="high">Haute</SelectItem>
                                                <SelectItem value="critical">Critique</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button type="submit" disabled={submitting} data-testid="submit-project-btn">
                                    {submitting ? "Création..." : "Créer le projet"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                        </div>
                    );
                })()}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher un projet..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                        data-testid="search-projects-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48" data-testid="status-filter">
                        <SelectValue placeholder="Tous les statuts" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les statuts</SelectItem>
                        <SelectItem value="planning">Planification</SelectItem>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="on_hold">En pause</SelectItem>
                        <SelectItem value="completed">Terminé</SelectItem>
                        <SelectItem value="cancelled">Annulé</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Projects Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedProjects.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                        <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Aucun projet trouvé</p>
                    </div>
                ) : (
                    paginatedProjects.map((project) => (
                        <Card
                            key={project.project_id}
                            className="cursor-pointer hover:shadow-md transition-all duration-200"
                            onClick={() => navigate(`/projects/${project.project_id}`)}
                            data-testid={`project-card-${project.project_id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <FolderKanban className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium">{project.name}</h4>
                                            {project.client_name && (
                                                <p className="text-sm text-muted-foreground">{project.client_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/projects/${project.project_id}`);
                                            }}>
                                                Voir détails
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                className="text-destructive"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(project.project_id);
                                                }}
                                            >
                                                Supprimer
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                <div className="flex items-center gap-2 mb-4">
                                    <Badge className={getStatusColor(project.status)}>
                                        {statusLabels[project.status] || project.status}
                                    </Badge>
                                    <Badge className={getPriorityColor(project.priority)}>
                                        {priorityLabels[project.priority] || project.priority}
                                    </Badge>
                                </div>

                                {project.description && (
                                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                        {project.description}
                                    </p>
                                )}

                                <div className="space-y-3">
                                    <div>
                                        <div className="flex items-center justify-between text-sm mb-1">
                                            <span className="text-muted-foreground">Progression</span>
                                            <span className="font-mono font-medium">{project.progress}%</span>
                                        </div>
                                        <Progress value={project.progress} className="h-2" />
                                    </div>

                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">
                                            {project.start_date ? formatDate(project.start_date) : "-"} → {project.end_date ? formatDate(project.end_date) : "-"}
                                        </span>
                                        {project.budget && (
                                            <span className="font-mono font-medium text-primary">
                                                {formatCurrency(project.budget)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            <Pagination
                page={currentPage}
                pages={totalPages}
                total={filteredProjects.length}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setCurrentPage(p)}
            />
        </div>
    );
};
